import React, { useCallback, useState } from 'react'
import styled from 'styled-components'
import Text from 'core-system/Text'
import { useDispatch, useSelector } from 'react-redux'
import { AppState } from 'redux/config/store'
import NoDataText from 'employee-platform/shared/components/NoDataText'
import FleetCard from './FleetCard'
import palette from 'core-system/Themes/palette'
import FleetCardDetailsModal from './FleetCardDetailsModal'
import AutoTopupSubscriptionDetails from './AutoTopupSubscriptionDetails'
import PrimaryButton from 'core-system/Button/PrimaryButton'
import SecondaryButton from 'core-system/Button/SecondaryButton'
import { employeePlatformActions } from 'redux/employeePlatform/employeePlatformSlice'
import { notificationsActions } from 'redux/notifications/notificationsSlice'
import {
  physicalCardShippingFromBilling,
  shouldShowRequestPhysicalCardButton,
} from '../requestPhysicalCard'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const CardsContainer = styled.div`
  display: flex;
  flex-direction: row;
  margin-top: 0.5rem;

  @media (max-width: ${(props) => props.theme.breakpoints[3]}) {
    flex-direction: column;
  }
`

const CardContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const CardType = styled(Text)`
  font-weight: 600;
  padding-bottom: 8px;
`

const RequestCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 1rem;
  max-width: 22rem;
`

const ConfirmActions = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
`

const CardImageContainer = styled(FleetCard)`
  margin-right: 16px;

  @media (max-width: ${(props) => props.theme.breakpoints[3]}) {
    margin-bottom: 16px;
  }
`

interface FleetCardsProps {
  isMobile: boolean
  showAutoTopupSubscriptions: boolean
}

const FleetCards = React.memo((props: FleetCardsProps) => {
  const { isMobile, showAutoTopupSubscriptions } = props
  const dispatch = useDispatch()

  const { cards } = useSelector(
    (state: AppState) => state.employeePlatform.wallet
  )
  const profileData = useSelector(
    (state: AppState) => state.employeePlatform.profileData
  )
  const physicalCardRequested = useSelector(
    (state: AppState) => state.employeePlatform.physicalCardRequested
  )

  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [selectedCard, setSelectedCard] = useState(null)
  const [confirmingPhysicalCard, setConfirmingPhysicalCard] = useState(false)

  const showRequestPhysicalCard = shouldShowRequestPhysicalCardButton({
    employerAllowsRequest: profileData?.requestPhysicalCard,
    cards,
    alreadyRequested: physicalCardRequested,
  })

  const submitPhysicalCardRequest = useCallback(() => {
    const shipping = physicalCardShippingFromBilling(profileData?.billingAddress)
    if (!shipping) {
      dispatch(
        notificationsActions.generalPageError(
          'Add a billing address before requesting a physical card.'
        )
      )
      setConfirmingPhysicalCard(false)
      return
    }
    dispatch(employeePlatformActions.orderPhysicalCard(shipping))
    setConfirmingPhysicalCard(false)
  }, [dispatch, profileData?.billingAddress])

  const handleCloseDetailsModal = useCallback(() => {
    setShowDetailsModal(false)
    setSelectedCard(null)
  }, [])

  const handleClickCard = useCallback(async (card) => {
    setSelectedCard(card)
    setShowDetailsModal(true)
  }, [])

  return (
    <Container>
      <Text variant='action1'>Fleet Cards</Text>
      <Text variant='body1' textColor={palette.grey.grey500}>
        Click on a card to view details
      </Text>
      <CardsContainer>
        {cards && cards.length > 0 ? (
          cards.map((card) => {
            return (
              <CardContainer key={card.internalId}>
                <CardType variant='body1' color={palette.primary.pink800}>
                  {card?.type[0].toUpperCase() + card?.type.slice(1)}
                </CardType>
                <CardImageContainer
                  card={card}
                  size={isMobile ? 'small' : 'large'}
                  onPress={handleClickCard}
                />
              </CardContainer>
            )
          })
        ) : (
          <NoDataText text='YOU DO NOT HAVE ANY FLEET CARDS' />
        )}
      </CardsContainer>
      {showRequestPhysicalCard ? (
        <RequestCard>
          {confirmingPhysicalCard ? (
            <>
              <Text variant='body1'>
                Are you sure you want to request a physical card?
              </Text>
              <ConfirmActions>
                <PrimaryButton onClick={submitPhysicalCardRequest}>
                  Confirm
                </PrimaryButton>
                <SecondaryButton
                  onClick={() => setConfirmingPhysicalCard(false)}
                >
                  Cancel
                </SecondaryButton>
              </ConfirmActions>
            </>
          ) : (
            <PrimaryButton onClick={() => setConfirmingPhysicalCard(true)}>
              Request Physical Card
            </PrimaryButton>
          )}
        </RequestCard>
      ) : null}
      <FleetCardDetailsModal
        open={showDetailsModal}
        closeModal={handleCloseDetailsModal}
        card={selectedCard}
        isMobile={isMobile}
      />
      {showAutoTopupSubscriptions ? <AutoTopupSubscriptionDetails /> : null}
    </Container>
  )
})

// Helps identify component in React error logs
if (process.env.NODE_ENV !== 'production') {
  FleetCards.displayName = 'FleetCards'
}

export default FleetCards
